import { Controller } from "@hotwired/stimulus"
import { loadRecaptcha } from "../src/shared/recaptcha_loader"

// reCAPTCHA v3 on a form, without Google's script in the page load: it costs ~0.5 s of main thread on a
// phone, in the way of the reader's first taps (INP). `load` fetches it (e.g. when the form's modal opens);
// `submit` holds the submission until it has a token, then submits again. Fetching the token at submit also
// keeps it fresh: a v3 token expires after 2 minutes.
//
// The token goes in `g-recaptcha-response-data[<action>]`, where the recaptcha gem's
// `verify_recaptcha(action:)` reads it. If the script is blocked or slow, the form submits without a token
// and the server answers with its usual error.
export default class extends Controller {
  static values = {
    siteKey: String,
    scriptUrl: String,
    action: String,
    timeout: { type: Number, default: 5000 }
  }

  disconnect () {
    this.submission = null
  }

  load () {
    this.recaptcha().catch(() => {})
  }

  async submit (event) {
    if (this.tokenReady) {
      this.tokenReady = false
      return
    }

    event.preventDefault()
    if (this.submission) return

    const submission = (this.submission = Symbol("submission"))
    const submitter = event.submitter
    this.responseInput().value = await this.token().catch(() => "")
    if (this.submission !== submission) return

    this.submission = null
    this.tokenReady = true
    this.element.requestSubmit(submitter?.form === this.element ? submitter : undefined)
  }

  async token () {
    const grecaptcha = await this.recaptcha()

    return grecaptcha.execute(this.siteKeyValue, { action: this.actionValue })
  }

  recaptcha () {
    return Promise.race([
      loadRecaptcha(this.scriptUrlValue, this.siteKeyValue),
      new Promise((_resolve, reject) => setTimeout(() => reject(new Error("reCAPTCHA timeout")), this.timeoutValue))
    ])
  }

  responseInput () {
    const name = `g-recaptcha-response-data[${this.actionValue}]`
    let input = Array.from(this.element.querySelectorAll("input[type='hidden']")).find((field) => field.name === name)

    if (!input) {
      input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      this.element.appendChild(input)
    }

    return input
  }
}
